import * as THREE from 'three';
import { WOLF_CLIPS, WOLF_GAITS, type WolfClip } from '../../../content/characters/rigs/wolf.js';
import type { Point, UnitDefinition, UnitState } from '../../../shared/game/world-types.js';
import { CHARACTER_ACTION_SECONDS, CHARACTER_CONTACT, type CharacterAction } from './character-animator.js';
import { createWolfModel, type WolfRig } from './wolf-model.js';
import { wolfAnimationClips } from './wolf-pose.js';
import type { UnitAnimator } from './unit-animator.js';
import type { HitImpact } from './character-hit.js';
import { HeadGlance, turnHead, type GlancePoint } from './head-glance.js';

const glanceEye = new THREE.Vector3();

const proceduralClips = new Map<number, THREE.AnimationClip[]>();
type Cue = { kind: CharacterAction; at: number };
type BonePose = { position: THREE.Vector3; rotation: THREE.Quaternion; scale: THREE.Vector3 };
/** Direct clip sampling on the authoritative clock: no mixer wall clock or locomotion root travel. */
export class WolfAnimator implements UnitAnimator {
  readonly rig: WolfRig;
  onStep: (() => void) | null = null;
  groundHeight?: (x: number, z: number) => number;
  private mixer: THREE.AnimationMixer;
  private actions = new Map<WolfClip, THREE.AnimationAction>();
  private scene: THREE.Object3D;
  private previous?: Point;
  private time?: number;
  private phase = 0;
  private movement = 0;
  private gallop = 0;
  private facing = 0;
  private action?: Cue;
  private reaction?: Cue;
  private deathAt?: number;
  private corpseSettled = false;
  animateSettledCorpses = false;
  get settledCorpse() { return this.corpseSettled; }
  private deathPose = new Map<THREE.Bone, BonePose>();
  private windupSeen?: number;
  private disposed = false;
  /** What the head glances at while the body keeps its heading (`head-glance.ts`). The scene sets it every frame. */
  glance?: GlancePoint;
  private headGlance = new HeadGlance();
  constructor(private definition: UnitDefinition) {
    this.rig = createWolfModel(definition); this.scene = this.rig.root;
    this.mixer = new THREE.AnimationMixer(this.scene);
    let clips=proceduralClips.get(this.rig.height);
    if(!clips){clips=wolfAnimationClips(this.rig);proceduralClips.set(this.rig.height,clips);}
    this.setClips(clips);
  }
  private setClips(clips: THREE.AnimationClip[]) {
    const missing=Object.keys(WOLF_CLIPS).filter(name=>!clips.some(clip=>clip.name===name));
    if (missing.length) throw new Error(`Missing wolf clips: ${missing.join(', ')}`);
    this.actions.clear();
    for (const clip of clips) if (clip.name in WOLF_CLIPS) {
      const action=this.mixer.clipAction(clip); action.play().setEffectiveWeight(0); this.actions.set(clip.name as WolfClip,action);
    }
  }
  reset() { this.previous=undefined;this.time=undefined;this.corpseSettled=false;this.phase=0;this.movement=0;this.gallop=0;this.facing=0;this.action=undefined;this.reaction=undefined;this.deathAt=undefined;this.deathPose.clear();this.windupSeen=undefined;this.headGlance.reset(); }
  stopActions() {this.action=undefined;this.reaction=undefined;}
  play(kind: CharacterAction, at: number, _toward?: Point, fromProgress=0, _impact?: HitImpact) {
    if (this.disposed) return;
    if (kind==='slash' && this.windupSeen!==undefined && at-this.windupSeen<.5) return;
    const cue={kind,at:at-(kind==='slash'?WOLF_CLIPS.attack:CHARACTER_ACTION_SECONDS[kind])*fromProgress};
    // A blow is physical (`hit-springs.ts`); the flinch clip is the wolf's guard.
    if (kind==='block') this.reaction=cue; else if (kind==='slash') this.action=cue;
  }
  swingLead(at: number) { return this.windupSeen!==undefined && at-this.windupSeen<.5 ? 0 : CHARACTER_CONTACT.slash*WOLF_CLIPS.attack; }
  playing() {
    if (!this.action || this.time===undefined) return undefined;
    const progress=(this.time-this.action.at)/WOLF_CLIPS.attack;
    return progress>=0&&progress<1?{kind:this.action.kind,progress}:undefined;
  }
  update(state: UnitState, position: Point, gameTime: number, target?: Point, inCombat=state.combat.targetId!==null) {
    if (this.disposed) return;
    if (this.time!==undefined && gameTime<this.time) this.reset();
    if (gameTime===this.time) return;
    if (!state.health.dead) this.corpseSettled=false;
    if (state.health.dead && this.corpseSettled && !this.animateSettledCorpses && this.previous?.x===position.x && this.previous.z===position.z) {this.time=gameTime;return;}
    const elapsed=this.time===undefined?0:gameTime-this.time, dt=Math.min(.25,elapsed);
    const dx=this.previous?position.x-this.previous.x:0,dz=this.previous?position.z-this.previous.z:0,distance=Math.hypot(dx,dz);
    const moving=elapsed>0 && distance>.0001 && distance<Math.max(10,this.definition.speed*2.5)*elapsed && !state.health.dead;
    const speed=moving?distance/elapsed:0, threshold=Math.min(2.2,this.definition.speed*.8);
    this.gallop=THREE.MathUtils.damp(this.gallop,state.combat.rush>0?1:THREE.MathUtils.smoothstep(speed,threshold-.35,threshold+.25),10,dt);
    const running=this.gallop>.5;
    this.movement=THREE.MathUtils.damp(this.movement,moving?1:0,12,dt);
    if (moving) {
      const gait=WOLF_GAITS[running?'run':'walk'], before=this.phase;
      this.phase+=distance/(THREE.MathUtils.lerp(WOLF_GAITS.walk.stride,WOLF_GAITS.run.stride,this.gallop)*this.rig.root.scale.x);
      // Emit every paw contact, including multiple contacts crossed in one rendered frame.
      for (const contact of Object.values(gait.contacts)) {
        const crossed = Math.floor(this.phase-contact+1e-9)-Math.floor(before-contact+1e-9);
        for (let i = 0; i < crossed; i++) this.onStep?.();
      }
    }
    const aim=target?{x:target.x-position.x,z:target.z-position.z}:moving?{x:dx,z:dz}:undefined;
    if (aim && Math.hypot(aim.x,aim.z)>.001 && !state.health.dead) {
      const desired=Math.atan2(aim.x,aim.z), delta=THREE.MathUtils.euclideanModulo(desired-this.facing+Math.PI,2*Math.PI)-Math.PI;
      this.facing+=delta*(elapsed?1-Math.exp(-12*dt):1);
    }
    const windup=state.combat.windup;
    if (!state.health.dead && windup?.kind==='attack') {
      this.windupSeen=gameTime;
      const into=CHARACTER_CONTACT.slash*WOLF_CLIPS.attack-windup.remaining;
      if (into>=0) this.action={kind:'slash',at:gameTime-into};
    }
    if (state.health.dead && this.deathAt===undefined) {
      this.deathAt=gameTime;
      this.scene.traverse(object=>{if(object instanceof THREE.Bone)this.deathPose.set(object,{position:object.position.clone(),rotation:object.quaternion.clone(),scale:object.scale.clone()});});
    } else if (!state.health.dead) {this.deathAt=undefined;this.deathPose.clear();}
    const samples=new Map<WolfClip,{weight:number;time:number}>();
    const add=(clip:WolfClip,weight:number,time:number)=>samples.set(clip,{weight,time});
    if (state.health.dead) add('death',1,Math.min(WOLF_CLIPS.death,gameTime-this.deathAt!));
    else {
      const cue=this.reaction && gameTime-this.reaction.at<WOLF_CLIPS.flinch?this.reaction:this.action;
      const clip:WolfClip=cue===this.reaction?'flinch':'attack', into=cue?gameTime-cue.at:-1;
      const active=into>=0&&into<WOLF_CLIPS[clip];
      const weight=active?THREE.MathUtils.smoothstep(into,0,.06)*(1-THREE.MathUtils.smoothstep(into/WOLF_CLIPS[clip],.84,1)):0;
      if (active) add(clip,weight,into);
      const movement=this.movement*(1-weight), base=(1-this.movement)*(1-weight);
      add('walk',movement*(1-this.gallop),(this.phase%1)*WOLF_CLIPS.walk);
      add('run',movement*this.gallop,(this.phase%1)*WOLF_CLIPS.run);
      add(inCombat?'combat_idle':'idle',base,gameTime%WOLF_CLIPS[inCombat?'combat_idle':'idle']);
    }
    for (const [name,action] of this.actions) {
      const sample=samples.get(name); action.setEffectiveWeight(sample?.weight??0);
      // Sample the code-generated clips on the content cue clock.
      action.time=Math.min(action.getClip().duration,(sample?.time??0)/WOLF_CLIPS[name]*action.getClip().duration);
    }
    this.mixer.update(0);
    if(state.health.dead && this.deathPose.size){
      const blend=THREE.MathUtils.smoothstep(gameTime-this.deathAt!,0,.18);
      for(const [bone,from] of this.deathPose){
        const target=bone.quaternion.clone();
        bone.position.lerpVectors(from.position,bone.position,blend);bone.scale.lerpVectors(from.scale,bone.scale,blend);
        bone.quaternion.copy(from.rotation).slerp(target,blend);
      }
      if(blend===1)this.deathPose.clear();
    }
    this.rig.root.rotation.y=this.facing;
    if (this.groundHeight && !state.health.dead) {
      const reach = .4, sin = Math.sin(this.facing), cos = Math.cos(this.facing);
      const forward = (this.groundHeight(position.x + sin * reach, position.z + cos * reach) - this.groundHeight(position.x - sin * reach, position.z - cos * reach)) / (reach * 2);
      const right = (this.groundHeight(position.x + cos * reach, position.z - sin * reach) - this.groundHeight(position.x - cos * reach, position.z + sin * reach)) / (reach * 2);
      this.rig.root.quaternion.setFromAxisAngle(new THREE.Vector3(0, 1, 0), this.facing)
        .multiply(new THREE.Quaternion().setFromEuler(new THREE.Euler(-Math.atan(forward), 0, Math.atan(right))));
    }
    // A glance turns the neck and head over the clips (the long neck takes more of it than a person's); a bite or a flinch lets it go.
    if (state.health.dead) this.headGlance.reset();
    else {
      const head=this.rig.joints.get('head');
      if (head) turnHead(this.rig.root,[{joint:this.rig.joints.get('chest'),yaw:.15,pitch:0},{joint:this.rig.joints.get('neck'),yaw:.5,pitch:.5},{joint:head,yaw:.35,pitch:.5}],
        this.headGlance.update(head.getWorldPosition(glanceEye),this.facing,this.glance,!this.playing()&&!(this.reaction&&gameTime-this.reaction.at<WOLF_CLIPS.flinch),dt));
    }
    this.previous={...position}; this.time=gameTime;
    this.corpseSettled=state.health.dead && this.deathAt!==undefined && gameTime-this.deathAt>=WOLF_CLIPS.death;
  }
  dispose() { if(this.disposed)return;this.disposed=true;this.deathPose.clear();this.mixer.stopAllAction();this.mixer.uncacheRoot(this.scene);disposeWolfScene(this.rig.root,this.scene===this.rig.root);this.rig.root.removeFromParent(); }
}

function disposeWolfScene(scene: THREE.Object3D, textures=false, skeletons=true) {
  const materials=new Set<THREE.Material>(), skins=new Set<THREE.Skeleton>(), maps=new Set<THREE.Texture>();
  scene.traverse(object=> {if(object instanceof THREE.SkinnedMesh)skins.add(object.skeleton);if(object instanceof THREE.Mesh){object.geometry.dispose();for(const material of Array.isArray(object.material)?object.material:[object.material])materials.add(material);}});
  for(const material of materials){if(material instanceof THREE.MeshStandardMaterial && material.map)maps.add(material.map);material.dispose();}
  if(skeletons)for(const skin of skins)skin.dispose(); if(textures)for(const map of maps)map.dispose();
}
